export const EARLY_STAGE_MEMBERSHIP = "Early Stage Membership";
export const BUSINESS_MEMBERSHIP = "Business Membership";
export const EXECUTIVE_MEMBERSHIP = "Executive Membership";
export const LEGACY_YOUTH_MEMBERSHIP = "Youth Membership";

function registrationMembershipSortIndex(name: string): number {
  const n = name.trim().toLowerCase();
  if (
    n === EARLY_STAGE_MEMBERSHIP.toLowerCase() ||
    n === LEGACY_YOUTH_MEMBERSHIP.toLowerCase()
  ) {
    return 0;
  }
  if (n === BUSINESS_MEMBERSHIP.toLowerCase()) return 1;
  if (n === EXECUTIVE_MEMBERSHIP.toLowerCase()) return 2;
  return 99;
}

/** Registration membership step: Early Stage → Business → Executive. */
export function sortMembershipsForRegistration<T extends { name: string }>(
  memberships: T[],
): T[] {
  return [...memberships].sort(
    (a, b) =>
      registrationMembershipSortIndex(a.name) -
      registrationMembershipSortIndex(b.name),
  );
}

export function isEarlyStageMembership(name?: string | null): boolean {
  return (name?.trim() ?? "") === EARLY_STAGE_MEMBERSHIP;
}

export function isLegacyYouthMembership(name?: string | null): boolean {
  return (name?.trim() ?? "") === LEGACY_YOUTH_MEMBERSHIP;
}

/** New Early Stage or grandfathered Youth members. */
export function isYouthOrEarlyStageMembership(name?: string | null): boolean {
  return isEarlyStageMembership(name) || isLegacyYouthMembership(name);
}

export function isBusinessOrExecutiveMembership(name?: string | null): boolean {
  const n = (name?.trim() ?? "").toLowerCase();
  return (
    n === BUSINESS_MEMBERSHIP.toLowerCase() ||
    n === EXECUTIVE_MEMBERSHIP.toLowerCase()
  );
}

/** Paid member: matches API admin member classification (not pre-checkout membership selection). */
export function isPaidPortalMember(user?: {
  hasPaid?: number | null;
  stripeStatus?: string | null;
} | null): boolean {
  if (!user) return false;
  if (user.hasPaid === 1) return true;
  const stripe = (user.stripeStatus ?? "").trim().toLowerCase();
  return stripe === "active" || stripe === "trialing";
}

/** Profile fields that imply an active membership, not a checkout selection. */
export const PROFILE_MEMBERSHIP_FIELD_KEYS = new Set([
  "membershipName",
  "isAnnualMembership",
]);

/** Same plan for upgrade/renew disable (Youth and Early Stage are one tier). */
export function isSameMembershipPlan(
  catalogName: string,
  currentName?: string | null,
): boolean {
  const a = catalogName.trim();
  const b = (currentName ?? "").trim();
  if (!a || !b) return false;
  if (a === b) return true;
  if (a.toLowerCase() === b.toLowerCase()) return true;
  if (isYouthOrEarlyStageMembership(a) && isYouthOrEarlyStageMembership(b)) {
    return true;
  }
  return false;
}

export function calculateAgeYears(dateOfBirth: string | Date): number | null {
  const dob = dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

export function isUnder23(dateOfBirth: string | Date): boolean {
  const age = calculateAgeYears(dateOfBirth);
  return age != null && age < 23;
}

/** Calendar date for API/DB (avoids timezone shifting DOB). */
export function toDateOnlyString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
